/**
 * Enhanced Incident Kanban Card Component
 * Improved card with rich information display using dnd-kit
 *
 * @component
 * @description Comprehensive incident card showing all relevant information:
 * - Priority & Severity badges
 * - Privacy & Compliance flags
 * - Customer information
 * - Team assignment
 * - Timeline information
 * - Due date alerts
 * - Evidence indicators
 * - Origin & Industry tags
 *
 * @example
 * ```tsx
 * <IncidentKanbanCardEnhanced
 *   incident={incident}
 *   onClick={(inc) => openWindow(inc)}
 * />
 * ```
 */

import { memo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Clock,
  Building2,
  User,
  AlertTriangle,
  Shield,
  Lock,
  Baby,
  Paperclip,
  Calendar,
  TrendingUp,
  Users,
  Flag,
  FileText,
  Eye,
  Edit,
  GitMerge,
  Archive,
  Trash2
} from 'lucide-react';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import type { Incident } from '@/types/incidents';
import { getSeverityLabel, getSeverityColor, getPriorityColor } from '@/lib/incidentHelpers';
import { cn } from '@/lib/utils';
import { KanbanCard } from '@/components/ui/shadcn-io/kanban';
import { mapLegacyStateToStatusReason } from './kanban-config';

interface IncidentKanbanCardEnhancedProps {
  incident: Incident;
  onClick: (incident: Incident) => void;
  onView?: (incident: Incident) => void;
  onEdit?: (incident: Incident) => void;
  onMerge?: (incident: Incident) => void;
  onArchive?: (incident: Incident) => void;
  onDelete?: (incident: Incident) => void;
}

/**
 * Get priority badge color based on priority level
 */
function getPriorityBadgeColor(priority?: string): string {
  if (!priority) return 'bg-gray-500 text-white hover:bg-gray-600';
  
  switch (priority) {
    case 'urgent':
      return 'bg-red-500 text-white hover:bg-red-600';
    case 'high':
      return 'bg-orange-500 text-white hover:bg-orange-600';
    case 'medium':
      return 'bg-yellow-500 text-white hover:bg-yellow-600';
    case 'low':
      return 'bg-blue-500 text-white hover:bg-blue-600';
    default:
      return 'bg-gray-500 text-white hover:bg-gray-600';
  }
}

/**
 * Check if incident is overdue
 */
function isOverdue(dueAt?: string | null): boolean {
  if (!dueAt) return false;
  return new Date(dueAt) < new Date();
}

/**
 * Calculate days until due or overdue
 */
function getDaysUntilDue(dueAt?: string | null): number | null {
  if (!dueAt) return null;
  const due = new Date(dueAt);
  const now = new Date();
  const diffTime = due.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
}

/**
 * Format industry label
 */
function getIndustryLabel(industry?: string): string {
  if (!industry) return 'N/A';
  
  const labels: Record<string, string> = {
    financial: 'Financiero',
    healthcare: 'Salud',
    telecom: 'Telecom',
    retail: 'Retail',
    technology: 'Tech',
    government: 'Gobierno',
    education: 'Educación',
    manufacturing: 'Manufactura',
    other: 'Otro',
  };
  return labels[industry] || industry;
}

/**
 * Memoized enhanced card component
 */
export const IncidentKanbanCardEnhanced = memo(function IncidentKanbanCardEnhanced({
  incident,
  onClick,
  onView,
  onEdit,
  onMerge,
  onArchive,
  onDelete
}: IncidentKanbanCardEnhancedProps) {
  const overdue = isOverdue(incident.dueAt);
  const daysUntilDue = getDaysUntilDue(incident.dueAt);
  const hasEvidences = incident.evidence && incident.evidence.length > 0;
  const hasParties = incident.parties && incident.parties.length > 0;
  const [doubleClickTimer, setDoubleClickTimer] = useState<ReturnType<typeof setTimeout> | null>(null);

  const handleClick = () => {
    if (doubleClickTimer) {
      // Double click detected - open edit mode
      clearTimeout(doubleClickTimer);
      setDoubleClickTimer(null);
      if (onEdit) {
        onEdit(incident);
      }
    } else {
      // First click - set timer for single/double click detection
      const timer = setTimeout(() => {
        setDoubleClickTimer(null);
        onClick(incident);
      }, 300); // 300ms threshold for double click
      setDoubleClickTimer(timer);
    }
  };

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ 
            duration: 0.3, 
            ease: [0.4, 0.0, 0.2, 1]
          }}
          whileHover={{ 
            scale: 1.02,
            transition: { duration: 0.2 }
          }}
          layout
        >
          <KanbanCard
            id={incident.id}
            name={incident.title || incident.incidentNumber}
            column={incident.statusReason || mapLegacyStateToStatusReason(incident.state || 'new')}
            className="cursor-pointer transition-all duration-300 hover:shadow-lg border-l-4"
            style={{
              borderLeftColor: overdue ? '#ef4444' : undefined,
            }}
          >
            <div
              role="button"
              tabIndex={0}
              onClick={handleClick}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onClick(incident);
                }
              }}
              aria-label={`Incidente ${incident.incidentNumber}: ${incident.title}`}
              className="flex flex-col gap-2 focus:outline-none focus:ring-2 focus:ring-primary rounded-md p-1"
            >
        {/* Header with badges */}
        <CardHeader className="pb-2 pt-3 px-3">
          <motion.div 
            className="flex items-start justify-between gap-2 mb-2"
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1, duration: 0.3 }}
          >
            <div className="flex gap-1.5">
              <AnimatePresence mode="wait">
                {incident.priority && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Badge className={cn('text-[10px] px-1.5 py-0.5 transition-transform hover:scale-110', getPriorityBadgeColor(incident.priority))}>
                      <Flag className="h-2.5 w-2.5 mr-0.5" />
                      {incident.priority ? String(incident.priority).toUpperCase() : 'N/A'}
                    </Badge>
                  </motion.div>
                )}
                {incident.severity && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.2, delay: 0.05 }}
                  >
                    <Badge className={cn('text-[10px] px-1.5 py-0.5 transition-transform hover:scale-110', getSeverityColor(incident.severity))}>
                      <AlertTriangle className="h-2.5 w-2.5 mr-0.5" />
                      {getSeverityLabel(incident.severity)}
                    </Badge>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>

          <motion.div
            className="flex items-start justify-between gap-2"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.3 }}
          >
            <div className="flex min-w-0 flex-1 flex-col">
              <CardTitle className="text-xs font-bold text-primary transition-colors duration-200">
                {incident.incidentNumber || 'Sin número'}
              </CardTitle>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (onEdit) {
                    onEdit(incident);
                  }
                }}
                className="mt-1 font-medium text-left text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:underline cursor-pointer transition-colors text-xs leading-tight line-clamp-2"
                title={`Abrir formulario de ${incident.title || 'Sin título'}`}
              >
                {incident.title || 'Sin título'}
              </button>
            </div>
          </motion.div>
        </CardHeader>

        <CardContent className="space-y-2 pt-0 px-3 pb-3">
          {/* Compliance & Privacy Flags */}
          {(incident.isPrivacy || incident.isBreach || incident.needsDpia || incident.involvesMinors) && (
            <div className="flex flex-wrap gap-1">
              {incident.isPrivacy && (
                <Badge variant="outline" className="text-[9px] px-1 py-0 border-purple-500 text-purple-700">
                  <Shield className="h-2 w-2 mr-0.5" />
                  Privacidad
                </Badge>
              )}
              {incident.isBreach && (
                <Badge variant="outline" className="text-[9px] px-1 py-0 border-red-500 text-red-700">
                  <Lock className="h-2 w-2 mr-0.5" />
                  Brecha
                </Badge>
              )}
              {incident.needsDpia && (
                <Badge variant="outline" className="text-[9px] px-1 py-0 border-orange-500 text-orange-700">
                  <FileText className="h-2 w-2 mr-0.5" />
                  DPIA
                </Badge>
              )}
              {incident.involvesMinors && (
                <Badge variant="outline" className="text-[9px] px-1 py-0 border-pink-500 text-pink-700">
                  <Baby className="h-2 w-2 mr-0.5" />
                  Menores
                </Badge>
              )}
            </div>
          )}

          {/* Type & Industry */}
          {(incident.type || incident.industry) && (
            <motion.div 
              className="flex items-center gap-2 text-[11px] text-muted-foreground"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4, duration: 0.3 }}
            >
              <Building2 className="h-3 w-3 shrink-0 transition-transform hover:scale-110" />
              <span className="truncate capitalize">
                {incident.type ? incident.type.replace(/_/g, ' ') : 'Sin tipo'}
                {incident.industry && ` · ${getIndustryLabel(incident.industry)}`}
              </span>
            </motion.div>
          )}

          {/* Customer Information - Prominent display */}
          {(incident.customerPerson || incident.customerCompany) && (
            <motion.div
              className="flex items-center gap-2 text-xs bg-muted/50 rounded-md px-2 py-1 -mx-1"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.45, duration: 0.3 }}
            >
              {incident.customerPerson ? (
                <User className="h-3.5 w-3.5 shrink-0 text-blue-500 transition-transform hover:scale-110" />
              ) : (
                <Building2 className="h-3.5 w-3.5 shrink-0 text-emerald-500 transition-transform hover:scale-110" />
              )}
              <span className="truncate font-semibold text-foreground">
                {incident.customerPerson?.fullName || incident.customerCompany?.tradeName || incident.customerCompany?.legalName || 'Sin cliente'}
              </span>
            </motion.div>
          )}

          {/* Team Assignment */}
          {incident.owningTeam && (
            <motion.div 
              className="flex items-center gap-2 text-[11px]"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5, duration: 0.3 }}
            >
              <Avatar className="h-5 w-5 transition-all hover:scale-110">
                <AvatarFallback className="text-[8px] bg-gradient-to-br from-blue-500 to-purple-600 text-white">
                  {incident.owningTeam?.name ? String(incident.owningTeam.name).substring(0, Math.min(2, incident.owningTeam.name.length)).toUpperCase() : 'T'}
                </AvatarFallback>
              </Avatar>
              <span className="truncate text-blue-600 font-medium">
                {incident.owningTeam?.name || 'Sin equipo'}
              </span>
            </motion.div>
          )}

          {/* Assigned To */}
          {incident.assignedTo && (
            <motion.div 
              className="flex items-center gap-2"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.55, duration: 0.3 }}
            >
              <Avatar className="h-5 w-5 border transition-all hover:scale-110">
                <AvatarFallback className="text-[9px] bg-gradient-to-br from-blue-400 to-purple-500 text-white">
                  {incident.assignedTo?.fullName
                    ? String(incident.assignedTo.fullName)
                      .split(' ')
                      .map((namePart) => namePart?.[0] || '')
                      .filter(Boolean)
                      .join('')
                      .toUpperCase()
                    : 'U'}
                </AvatarFallback>
              </Avatar>
              <span className="truncate text-[11px] font-medium">
                {incident.assignedTo.fullName}
              </span>
            </motion.div>
          )}

          {/* Due Date with Alert */}
          {incident.dueAt && (
            <div className={cn(
              "flex items-center gap-2 text-[11px] font-medium",
              overdue ? "text-red-600" : daysUntilDue !== null && daysUntilDue <= 3 ? "text-orange-600" : "text-muted-foreground"
            )}>
              <Calendar className="h-3 w-3 shrink-0" />
              <span>
                Vence: {new Date(incident.dueAt).toLocaleDateString('es-CL', {
                  month: 'short',
                  day: 'numeric',
                })}
                {daysUntilDue !== null && (
                  <span className="ml-1">
                    ({daysUntilDue > 0 ? `${daysUntilDue}d` : overdue ? 'Vencido' : 'Hoy'})
                  </span>
                )}
              </span>
            </div>
          )}

          {/* Created Date */}
          {incident.createdAt && (
            <motion.div 
              className="flex items-center gap-2 text-[11px] text-muted-foreground"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.55, duration: 0.3 }}
            >
              <Clock className="h-3 w-3 shrink-0 transition-transform hover:rotate-12" />
              <span>
                Creado: {new Date(incident.createdAt).toLocaleDateString('es-CL', {
                  month: 'short',
                  day: 'numeric',
                  year: '2-digit',
                })}
              </span>
            </motion.div>
          )}

          {/* Footer with counters */}
          {(hasEvidences || hasParties || incident.impact) && (
            <motion.div 
              className="flex items-center justify-between pt-2 border-t"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.65, duration: 0.3 }}
            >
              {/* Evidence & Parties Counter */}
              <div className="flex items-center gap-3">
                {hasEvidences && (
                  <motion.div 
                    className="flex items-center gap-1 text-[10px] text-muted-foreground"
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Paperclip className="h-3 w-3 transition-transform hover:rotate-12" />
                    <span className="font-medium">{incident.evidence?.length || 0}</span>
                  </motion.div>
                )}
                {hasParties && (
                  <motion.div 
                    className="flex items-center gap-1 text-[10px] text-muted-foreground"
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Users className="h-3 w-3 transition-transform hover:scale-110" />
                    <span className="font-medium">{incident.parties?.length || 0}</span>
                  </motion.div>
                )}
              </div>

              {/* Impact indicator */}
              {incident.impact && (
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.2 }}
                >
                  <Badge variant="outline" className="text-[9px] px-1.5 py-0 transition-all hover:bg-accent">
                    <TrendingUp className="h-2 w-2 mr-0.5" />
                    Impacto: {incident.impact ? String(incident.impact).toUpperCase() : 'N/A'}
                  </Badge>
                </motion.div>
              )}
            </motion.div>
          )}
        </CardContent>
      </div>
    </KanbanCard>
        </motion.div>
      </ContextMenuTrigger>
      
      <ContextMenuContent className="w-48">
        {onView && (
          <ContextMenuItem
            onClick={(e) => {
              e.stopPropagation();
              onView(incident);
            }}
            className="cursor-pointer"
          >
            <Eye className="mr-2 h-4 w-4" />
            Ver
          </ContextMenuItem>
        )}
        {onEdit && (
          <ContextMenuItem
            onClick={(e) => {
              e.stopPropagation();
              onEdit(incident);
            }}
            className="cursor-pointer"
          >
            <Edit className="mr-2 h-4 w-4" />
            Editar
          </ContextMenuItem>
        )}
        {(onView || onEdit) && (onMerge || onArchive || onDelete) && <ContextMenuSeparator />}
        {onMerge && (
          <ContextMenuItem
            onClick={(e) => {
              e.stopPropagation();
              onMerge(incident);
            }}
            className="cursor-pointer"
          >
            <GitMerge className="mr-2 h-4 w-4" />
            Fusionar
          </ContextMenuItem>
        )}
        {onArchive && (
          <ContextMenuItem
            onClick={(e) => {
              e.stopPropagation();
              onArchive(incident);
            }}
            className="cursor-pointer"
          >
            <Archive className="mr-2 h-4 w-4" />
            Archivar
          </ContextMenuItem>
        )}
        {(onMerge || onArchive) && onDelete && <ContextMenuSeparator />}
        {onDelete && (
          <ContextMenuItem
            onClick={(e) => {
              e.stopPropagation();
              onDelete(incident);
            }}
            className="cursor-pointer text-destructive focus:text-destructive"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Eliminar
          </ContextMenuItem>
        )}
      </ContextMenuContent>
    </ContextMenu>
  );
});
